import { useCallback, useMemo } from 'react';
import { LocalDate, LocalTime } from 'js-joda';
import { DndContext } from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import DragOverlayItem from 'components/library/DragOverlayItem';
import Typography from 'components/library/Typography';
import withBox from 'helpers/withBox';
import withStyles from 'helpers/withStyles';
import withTranslations from 'helpers/withTranslations';
import { useNow } from 'hooks/useNow';
import { formatTime } from 'utils/time';
import SchedulerBlock from './components/SchedulerBlock';
import SchedulerIntentCell from './components/SchedulerIntentCell';
import SortableSchedulerBlock from './components/SortableSchedulerBlock';
import { usePendingScheduleModel } from './hooks/usePendingScheduleModel';
import { useSchedulerDrag } from './hooks/useSchedulerDrag';
import { getBlockId } from './utils/getBlockId';
import { layoutSchedule } from 'utils/schedules/layout/layoutSchedule';
import { toStoredModel } from 'utils/schedules/layout/toStoredModel';
import { normalizeTime } from '../../../../utils/schedules/normalizeTime';

const i18nPath = 'components.app.schedules.Scheduler';

const timeFormat = 'h:mm a';

const styles = theme => ({
  root: {
    tableLayout: 'fixed',
    width: '100%',
    height: 'fit-content',
    borderSpacing: 0,

    '& tr': {
      height: '100%',
    },
  },
  time: {
    position: 'relative',                                    // required for fills
    width: '4.85rem',
    textAlign: 'center',
    padding: theme.spacing.unit,
    background: theme.palette.grey['50'],
    borderColor: theme.palette.divider,
    borderStyle: 'solid',
    borderWidth: `0 1px 1px 0`,
    whiteSpace: 'nowrap',
  },
  timeFill: {
    position: 'absolute',
    top: 0,
    background: 'rgba(0, 0, 0, 0.06)',
    width: '100%',
    height: '100%',
    marginLeft: -theme.spacing.unit, // same as `padding` for `time` style
  },
  intent: {
    padding: 0,
    verticalAlign: 'top',
    borderBottom: `solid 1px ${theme.palette.divider}`,

    '&:hover': {
      background: theme.palette.grey['100'],
    }
  },
});

function calculateFill(block, timeOffset, nowOffset, interval, compact) {
  // If the block has a conflict, use the duration of the time block, not the intent
  const duration = compact && block.intent
    ? block.intent.duration - (block.conflictDuration ?? 0)
    : interval;

  return timeOffset > nowOffset ? 0 : Math.min(1, (nowOffset - timeOffset) / duration);
}

const Scheduler = ({
  schedule,
  interval = 15,
  intents = [],
  actionMode,                           // Current options: edit, statusReadOnly, statusChange
  compact = false,
  variant = 'schedule:scheduling',
  fillActive = false,
  header,
  emptyComponent,
  onScheduleViewUpdate,
  onDragPersist,
  onIntentSchedule = () => {},
  onIntentUpdate,
  onIntentDelete,
  onStatusChange = () => {},
  onScheduleExtend,
  classes,
  t,
}) => {
  // A schedule with no start/end time can briefly reach this component while the schedule is
  // still loading (e.g. navigating straight to a schedule URL before `querySchedulesByDate`
  // resolves), or for a schedule document that never had its times set. `normalizeTime` throws on
  // an undefined value, which crashes the whole render tree, so render nothing until the schedule
  // has times. (This guard also makes the component robust for e2e navigation.)
  const hasScheduleTimes = schedule.startTime != null && schedule.endTime != null;

  // HACK: See note in `normalizeTime` why this may no longer be needed. [twl 22.Jun.23]
  const startTime = hasScheduleTimes ? normalizeTime(schedule.startTime) : LocalTime.MIDNIGHT;
  const endTime = hasScheduleTimes ? normalizeTime(schedule.endTime) : LocalTime.MIDNIGHT;
  const {
    nowOffset, isNowActive
  } = useNow(LocalDate.now(), startTime, null, !fillActive ? 0 : undefined);
  const unscheduledIntents = intents.filter(intent => !intent.start);

  // The stored-model layout: the SAME engine renders the normal schedule and the drag
  // preview, so what a drag shows and what re-renders after its drop are one computation.
  // Memoized so drag re-renders never re-run the layout. `effectiveModel` is the stored
  // model with any render-side repairs (duplicate-start bumps, off-grid snaps) baked in —
  // drags compose on it and edits persist it, so repairs self-heal on the next write;
  // render-skipped blocks (unloaded todos) stay in it so a persist can never silently drop
  // them. `startTime`/`endTime` derive from `schedule`, which the deps cover.
  const layoutState = useMemo(
    () => {
      if (!hasScheduleTimes) {
        return { model: [], intentsByTodoId: {}, blocks: [], effectiveModel: [] };
      }

      const { model, intentsByTodoId } = toStoredModel(schedule, intents);
      const laidOut = layoutSchedule(model, intentsByTodoId, { startTime, endTime, interval });

      const renderedStartByTodo = new Map(
        laidOut
          .filter(block => block.intent?.todo?.id != null)
          .map(block => [`${block.intent.todo.id}`, block.start.toString()])
      );
      const effectiveModel = model.map(entry => {
        const renderedStart = renderedStartByTodo.get(entry.todo);

        return renderedStart && renderedStart !== entry.start
          ? { ...entry, start: renderedStart }
          : entry;
      });

      return { model, intentsByTodoId, blocks: laidOut, effectiveModel };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ hasScheduleTimes, schedule, intents, interval ],
  );

  // The pending model makes drops APPEAR instantaneous: from the moment a drag ends, the
  // schedule renders the dropped model (locally authoritative) while persistence settles in
  // the background, strictly sequentially. Snapshots reconcile: our own echo settles it, an
  // external write wins over it.
  const { pendingModel, commitDrop } = usePendingScheduleModel({
    schedule,
    snapshotModel: layoutState.effectiveModel,
    persist: onDragPersist,
  });

  const renderState = useMemo(
    () => {
      if (!pendingModel) {
        return layoutState;
      }

      const blocks = layoutSchedule(
        pendingModel, layoutState.intentsByTodoId, { startTime, endTime, interval },
      );

      return { ...layoutState, blocks, effectiveModel: pendingModel };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ layoutState, pendingModel, interval ],
  );

  const { blocks, draggingItem, dndProps } = useSchedulerDrag({
    layoutState: renderState,
    startTime,
    endTime,
    interval,
    compact,
    onDrop: commitDrop,
  });

  const intentCount = blocks.reduce((count, block) => count + (block.intent ? 1 : 0), 0);

  const doIntentUpdate = useCallback(async (intent, changes) => {
    if (schedule.views?.schedule) {
      // Persist from the stored model (with render-side repairs baked in), never from
      // the rendered block list: blocks the renderer skips (a todo that hasn't loaded,
      // a corrupt start) stay in the model, so this write can never silently delete
      // them. An edited start is substituted directly.
      const model = renderState.effectiveModel.map(entry =>
        entry.todo === `${intent.todo.id}` && changes.start !== undefined
          ? { ...entry, start: changes.start ? `${changes.start}` : undefined }
          : entry
      );

      await onScheduleViewUpdate(schedule, model);
    }

    await onIntentUpdate(intent, changes);
  }, [schedule, renderState, onScheduleViewUpdate, onIntentUpdate]);

  const columns = {
    time: {
      className: classes.time,
      ...isNowActive && {
        fillClass: classes.timeFill,
        fill: (block, time) => calculateFill(block, time.offset, nowOffset, interval, compact),
      },
      render: time => (
        <Typography variant="body2" color="textSecondary">
          {formatTime(time, timeFormat)}
        </Typography>
      ),
    },
    intent: {
      className: classes.intent,
      state: block => block.intent ? 'intent' : (actionMode === 'edit' ? 'editor' : 'empty'),
      render: block => (
        <SchedulerIntentCell
          block={block}
          variant={variant}
          actionMode={actionMode}
          unscheduledIntents={unscheduledIntents}
          onIntentUpdate={doIntentUpdate}
          onIntentDelete={onIntentDelete}
          onStatusChange={onStatusChange}
          onIntentSchedule={onIntentSchedule}
          onScheduleExtend={onScheduleExtend}
        />
      ),
    }
  };

  const ids = blocks.map(getBlockId);

  // No schedule times yet (still loading, or a schedule without times): render the header/empty
  // state rather than a bogus midnight-to-midnight grid. See the `hasScheduleTimes` note above.
  if (!hasScheduleTimes) {
    return (
      <div>
        {header}
        {emptyComponent}
      </div>
    );
  }

  const scheduler = (
    <div>
      {header}

      {intentCount === 0 && emptyComponent}

      {(intentCount > 0 || !emptyComponent) &&
        <table className={classes.root}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {blocks.map(block =>
              <SortableSchedulerBlock
                key={getBlockId(block)}
                id={getBlockId(block)}
                block={block}
                columns={columns}
                compact={compact}
                disabled={block.intent?.todo?.type === 'appointment'}
              />
            )}
          </SortableContext>
        </table>
      }
    </div>
  );

  return (
    <DndContext {...dndProps}>
      {scheduler}

      {<DragOverlayItem
        item={draggingItem &&
          <table className={classes.root}>
            <SchedulerBlock
              block={draggingItem}
              columns={columns}
              compact={compact}
              id={getBlockId(draggingItem)}
            />
          </table>
        }
        modifiers={[restrictToWindowEdges]}
      />}
    </DndContext>
  );
};

export default withBox(withStyles(styles)(withTranslations(Scheduler)), { i18nPath });
